import openAISystemMessage from 'common/src/modules/shared/copilot/openAI/utils/openAISystemMessage';
import openAIUserMessage from 'common/src/modules/shared/copilot/openAI/utils/openAIUserMessage';
import {ChatUtilsInvokeStandaloneLLMCallParams} from 'common/src/modules/shared/copilot/copilot.types';
import {ZodTypeAny, z} from 'zod';

type InvokeStandaloneLLMCall = <T extends ZodTypeAny>(
  params: ChatUtilsInvokeStandaloneLLMCallParams<T>,
) => Promise<z.infer<T>>;

type GenerateInlineWidgetTitleParams = {
  invokeStandaloneLLMCall: InvokeStandaloneLLMCall;
  callChain: ChatUtilsInvokeStandaloneLLMCallParams<ZodTypeAny>['callChain'];
  fallbackTitle: string;
  widgetKindDescription: string;
  campaignName?: string;
  metrics: string[];
  dimensions: string[];
  startDate: string;
  endDate: string;
};

async function generateInlineWidgetTitle({
  invokeStandaloneLLMCall,
  callChain,
  fallbackTitle,
  widgetKindDescription,
  campaignName,
  metrics,
  dimensions,
  startDate,
  endDate,
}: GenerateInlineWidgetTitleParams): Promise<string> {
  const systemPrompt = `You are an expert analytics assistant that generates concise, human-friendly titles for analytics widgets.
The title must clearly describe the data being shown using the metrics and dimensions.
The title must never mention more than 2 metrics.
Keep the title short and readable (ideally under 80 characters).`;

  const userPromptParts: string[] = [];

  if (campaignName) {
    userPromptParts.push(
      `Create a title for a "${widgetKindDescription}" analytics widget for campaign "${campaignName}".`,
    );
  } else {
    userPromptParts.push(
      `Create a title for a "${widgetKindDescription}" account-level analytics widget (not tied to a specific campaign).`,
    );
  }

  if (metrics.length) {
    userPromptParts.push(`Metrics: ${metrics.join(', ')}.`);
  }

  userPromptParts.push(`Dimensions: ${dimensions.join(', ')}.`);

  userPromptParts.push(`Date range: from ${startDate} to ${endDate}.`);

  const messages = [
    openAISystemMessage(systemPrompt),
    openAIUserMessage(userPromptParts.join(' ')),
  ];

  try {
    const response = await invokeStandaloneLLMCall({
      messages,
      responseSchema: z.object({
        title: z
          .string()
          .describe(
            'A concise, human-friendly widget title that clearly describes the data, and is ideally under 80 characters.',
          ),
      }),
      callChain,
    });

    if (response.title) {
      return response.title;
    }
  } catch (error) {
    // Ignore and fall back to the provided fallback title.
  }

  return fallbackTitle;
}

export default generateInlineWidgetTitle;
